import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import { ChangePasswordRequest } from "../../../app/models/change-password";
import { authServices } from "../../../app/services/auth.services";
import { authActions, useAppDispatch } from "../../../redux";

export interface ChangePasswordDialogProps {
  show: boolean;
  setShow: (show: boolean) => void;
}

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)\S{8,16}$/;

type FieldErrors = {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
};

type FieldTouched = {
  currentPassword: boolean;
  newPassword: boolean;
  confirmPassword: boolean;
};

const INITIAL_TOUCHED: FieldTouched = {
  currentPassword: false,
  newPassword: false,
  confirmPassword: false,
};

export default function ChangePasswordDialog({
  show,
  setShow,
}: ChangePasswordDialogProps) {
  const dispatch = useAppDispatch();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<FieldTouched>(INITIAL_TOUCHED);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    success: boolean;
    msg: string;
  }>({ open: false, success: true, msg: "" });

  const resetAndClose = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setFieldErrors({});
    setTouched(INITIAL_TOUCHED);
    setShow(false);
  };

  const validateCurrentPassword = (value: string): string | undefined =>
    value ? undefined : "Ingrese su contraseña actual.";

  const validateNewPassword = (
    value: string,
    current: string
  ): string | undefined => {
    if (!PASSWORD_PATTERN.test(value)) {
      return "Debe tener entre 8 y 16 caracteres, sin espacios, con al menos una mayúscula, una minúscula y un número.";
    }
    if (current && value === current) {
      return "La nueva contraseña debe ser distinta de la actual.";
    }
    return undefined;
  };

  const validateConfirmPassword = (
    value: string,
    newValue: string
  ): string | undefined =>
    value !== newValue ? "Las contraseñas no coinciden." : undefined;

  const validate = (): FieldErrors => ({
    currentPassword: validateCurrentPassword(currentPassword),
    newPassword: validateNewPassword(newPassword, currentPassword),
    confirmPassword: validateConfirmPassword(confirmPassword, newPassword),
  });

  const handleBlurField = (field: keyof FieldErrors) => {
    if (!touched[field]) return;
    setFieldErrors((prev) => {
      switch (field) {
        case "currentPassword":
          return {
            ...prev,
            currentPassword: validateCurrentPassword(currentPassword),
          };
        case "newPassword":
          return {
            ...prev,
            newPassword: validateNewPassword(newPassword, currentPassword),
          };
        case "confirmPassword":
          return {
            ...prev,
            confirmPassword: validateConfirmPassword(
              confirmPassword,
              newPassword
            ),
          };
        default:
          return prev;
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    const data: ChangePasswordRequest = {
      currentPassword,
      newPassword,
      confirmPassword,
    };

    setSaving(true);
    authServices
      .changePassword(data)
      .then(() => {
        resetAndClose();
        setToast({
          open: true,
          success: true,
          msg: "Contraseña actualizada correctamente. Por seguridad, tu sesión se cerrará.",
        });
        setTimeout(() => dispatch(authActions.logout()), 2000);
      })
      .catch((error: any) => {
        const apiErrors = error?.response?.data?.errors;
        if (apiErrors) {
          setFieldErrors({
            currentPassword: apiErrors.currentPassword?.[0]?.message,
            newPassword: apiErrors.newPassword?.[0]?.message,
            confirmPassword: apiErrors.confirmPassword?.[0]?.message,
          });
        } else {
          setToast({
            open: true,
            success: false,
            msg: "No se pudo actualizar la contraseña. Intente nuevamente.",
          });
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <Dialog
        open={show}
        onClose={() => !saving && resetAndClose()}
        fullWidth
        maxWidth="xs"
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>Cambiar Contraseña</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Contraseña actual"
              type={showPasswords ? "text" : "password"}
              fullWidth
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setTouched((prev) => ({ ...prev, currentPassword: true }));
                if (fieldErrors.currentPassword) {
                  setFieldErrors((prev) => ({
                    ...prev,
                    currentPassword: undefined,
                  }));
                }
              }}
              onBlur={() => handleBlurField("currentPassword")}
              error={!!fieldErrors.currentPassword}
              helperText={fieldErrors.currentPassword}
              autoComplete="current-password"
            />
            <TextField
              margin="dense"
              label="Nueva contraseña"
              type={showPasswords ? "text" : "password"}
              fullWidth
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setTouched((prev) => ({ ...prev, newPassword: true }));
                if (fieldErrors.newPassword) {
                  setFieldErrors((prev) => ({
                    ...prev,
                    newPassword: undefined,
                  }));
                }
              }}
              onBlur={() => handleBlurField("newPassword")}
              error={!!fieldErrors.newPassword}
              helperText={
                fieldErrors.newPassword ||
                "8 a 16 caracteres, sin espacios, con mayúscula, minúscula y número."
              }
              autoComplete="new-password"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPasswords((v) => !v)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPasswords ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              margin="dense"
              label="Confirmar nueva contraseña"
              type={showPasswords ? "text" : "password"}
              fullWidth
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setTouched((prev) => ({ ...prev, confirmPassword: true }));
                if (fieldErrors.confirmPassword) {
                  setFieldErrors((prev) => ({
                    ...prev,
                    confirmPassword: undefined,
                  }));
                }
              }}
              onBlur={() => handleBlurField("confirmPassword")}
              error={!!fieldErrors.confirmPassword}
              helperText={fieldErrors.confirmPassword}
              autoComplete="new-password"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={resetAndClose} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={18} /> : "Guardar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={toast.success ? "success" : "error"}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
